import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, ScanFace, Check, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, formatErr, previewUrl, rupiah } from "@/lib/api";
import { DateFolders, formatFolderDate } from "@/components/DateFolders";

const LIMIT = 100;

const PriceEditor = ({ photo, onSaved }) => {
  const [v, setV] = useState(photo.price);
  const [open, setOpen] = useState(false);
  const save = async () => {
    try {
      onSaved((await api.patch(`/photos/${photo.id}`, { price: Number(v) })).data);
      setOpen(false);
      toast.success("Harga foto diperbarui");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="font-mono-x text-xs font-semibold text-zinc-800 hover:text-blue-600 underline decoration-dotted underline-offset-2" data-testid={`pg-photo-price-${photo.id}`}>{rupiah(photo.price)}</button>
      </PopoverTrigger>
      <PopoverContent className="w-56 rounded-xl space-y-2">
        <p className="text-xs font-semibold text-zinc-500">Harga jual foto ini (Rp)</p>
        <Input type="number" min={0} value={v} onChange={(e) => setV(e.target.value)} className="h-9" data-testid={`pg-photo-price-input-${photo.id}`} />
        <Button size="sm" onClick={save} className="w-full rounded-lg bg-blue-600 hover:bg-blue-700" data-testid={`pg-photo-price-save-${photo.id}`}>Simpan</Button>
      </PopoverContent>
    </Popover>
  );
};

const BulkBar = ({ count, onPrice, onDelete, onClear }) => {
  const [price, setPrice] = useState("");
  return (
    <div className="sticky top-20 z-30 mb-4 rounded-2xl bg-zinc-900 text-white p-3 flex flex-wrap items-center gap-3 shadow-lg" data-testid="bulk-bar">
      <span className="text-sm font-semibold px-2" data-testid="bulk-selected-count">{count} foto dipilih</span>
      <div className="flex items-center gap-2 ml-auto">
        <Input type="number" min={0} placeholder="Harga baru (Rp)" value={price} onChange={(e) => setPrice(e.target.value)} className="h-9 w-40 bg-white text-zinc-900" data-testid="bulk-price-input" />
        <Button size="sm" disabled={price === ""} onClick={() => onPrice(Number(price))} className="rounded-lg bg-blue-600 hover:bg-blue-700" data-testid="bulk-price-button">Ubah harga</Button>
        <Button size="sm" variant="destructive" onClick={onDelete} className="rounded-lg" data-testid="bulk-delete-button"><Trash2 className="h-4 w-4 mr-1" />Hapus</Button>
        <Button size="sm" variant="ghost" onClick={onClear} className="rounded-lg text-zinc-300 hover:text-white hover:bg-zinc-800" data-testid="bulk-clear-button">Batal</Button>
      </div>
    </div>
  );
};

const FeaturedButton = ({ active, custom, onClick, id }) => (
  <button onClick={onClick} title={active && custom ? "Batalkan foto profil publik" : "Jadikan foto profil publik"}
    className={`absolute top-2 right-2 h-7 rounded-md px-1.5 inline-flex items-center gap-1 text-[10px] font-bold border transition-[opacity,background-color] ${active ? "bg-amber-400 border-amber-400 text-zinc-900 opacity-100" : "bg-white/90 border-zinc-300 text-zinc-600 opacity-0 group-hover:opacity-100 focus:opacity-100 max-sm:opacity-100"}`}
    data-testid={`pg-photo-featured-${id}`}>
    <Star className={`h-3.5 w-3.5 ${active ? "fill-zinc-900" : ""}`} />{active ? (custom ? "Foto profil" : "Profil (otomatis)") : ""}
  </button>
);

export const PhotoManager = ({ eventId, reloadKey, onChanged }) => {
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [allMode, setAllMode] = useState(false);
  const [date, setDate] = useState(null);
  const [foldersKey, setFoldersKey] = useState(0);
  const [featured, setFeatured] = useState({ photo_id: null, is_custom: false });
  useEffect(() => { api.get("/photographer/featured").then((r) => setFeatured(r.data)).catch(() => {}); }, [reloadKey]);
  const toggleFeatured = async (pid) => {
    const unset = featured.is_custom && featured.photo_id === pid;
    try {
      const { data } = await api.put("/photographer/featured", { photo_id: unset ? null : pid });
      setFeatured(data);
      toast.success(unset ? "Foto profil publik kembali otomatis memakai foto terbaru" : "Dijadikan foto profil publik");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const fetchPage = useCallback(async (p) => {
    const { data } = await api.get(`/events/${eventId}`, { params: { page: p, limit: LIMIT, ...(date ? { date } : {}) } });
    setPhotos((prev) => (p === 1 ? data.photos : [...prev, ...data.photos]));
    setTotal(data.total);
    setHasMore(data.has_more);
    setPage(p);
  }, [eventId, date]);

  useEffect(() => { fetchPage(1); }, [fetchPage, reloadKey]);
  useEffect(() => { setSelected(new Set()); setAllMode(false); }, [date]);

  const clear = () => { setSelected(new Set()); setAllMode(false); };
  const toggle = (id) => {
    setAllMode(false);
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const count = allMode ? total : selected.size;

  const bulk = async (body) => {
    try {
      const { data } = await api.post(`/events/${eventId}/photos/bulk`, { ...body, all: allMode, date: allMode ? date : null, photo_ids: allMode ? [] : [...selected] });
      toast.success(`${data.updated} foto diperbarui`);
      clear();
      fetchPage(1);
      setFoldersKey((k) => k + 1);
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const del = () => { if (window.confirm(`Hapus ${count} foto?`)) bulk({ action: "delete" }); };
  const replace = (p) => setPhotos((prev) => prev.map((x) => (x.id === p.id ? { ...x, price: p.price } : x)));

  return (
    <section>
      <DateFolders eventId={eventId} value={date} onChange={setDate} reloadKey={`${reloadKey}-${foldersKey}`} testPrefix="pg-date-folder" />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-zinc-600"><span className="font-bold text-zinc-900" data-testid="pg-photo-total">{total}</span> foto{date ? ` di folder ${formatFolderDate(date)}` : ""} · menampilkan {photos.length}</p>
        {total > 0 && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-lg bg-white" onClick={() => { setAllMode(false); setSelected(new Set(photos.map((p) => p.id))); }} data-testid="select-visible-button">Pilih yang tampil</Button>
            <Button size="sm" variant="outline" className="rounded-lg bg-white" onClick={() => { setAllMode(true); setSelected(new Set()); }} data-testid="select-all-button">{date ? `Pilih semua di folder ini (${total})` : `Pilih semua (${total})`}</Button>
          </div>
        )}
      </div>
      {count > 0 && <BulkBar count={count} onPrice={(price) => bulk({ action: "price", price })} onDelete={del} onClear={clear} />}
      {total === 0 ? <p className="text-zinc-500 py-10 text-center" data-testid="pg-photo-empty">Belum ada foto.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pg-photo-grid">
          {photos.map((p) => {
            const isSel = allMode || selected.has(p.id);
            return (
              <div key={p.id} className={`group relative rounded-xl overflow-hidden border bg-white ${isSel ? "border-blue-500 ring-2 ring-blue-500/30" : "border-zinc-200"}`} data-testid={`pg-photo-${p.id}`}>
                <button onClick={() => toggle(p.id)} className="block w-full" data-testid={`pg-photo-select-${p.id}`}>
                  <img src={previewUrl(p.id)} alt="" loading="lazy" className="w-full aspect-[4/3] object-cover bg-zinc-100" />
                </button>
                <FeaturedButton active={featured.photo_id === p.id} custom={featured.is_custom} onClick={() => toggleFeatured(p.id)} id={p.id} />
                {p.hd_ready && <span className="absolute top-10 right-2 rounded-md bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid={`pg-photo-hd-${p.id}`}>HD</span>}
                <span className={`absolute top-2 left-2 h-6 w-6 rounded-md grid place-items-center border ${isSel ? "bg-blue-600 border-blue-600 text-white" : "bg-white/90 border-zinc-300 text-transparent"}`}><Check className="h-4 w-4" /></span>
                <div className="flex items-center justify-between px-2.5 py-2">
                  <span className="inline-flex items-center gap-1 text-xs text-zinc-500" data-testid={`pg-photo-faces-${p.id}`}><ScanFace className="h-3 w-3 text-blue-600" />{p.face_count}</span>
                  <PriceEditor photo={p} onSaved={replace} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {hasMore && (
        <div className="text-center mt-8">
          <Button variant="outline" className="rounded-xl bg-white" onClick={() => fetchPage(page + 1)} data-testid="pg-load-more-button">Muat lebih banyak</Button>
        </div>
      )}
    </section>
  );
};
